import { z } from 'zod';
import { PlayerSchema } from '../../models/player';
import { SessionSchema, SessionTokenSchema } from '../../models/session';
import { API_PREFIX, DEV_PREFIX, defineEndpoint } from '../endpoint';

/**
 * Browser routes of the Google sign-in (ADR 0029). They are redirects, not JSON endpoints:
 * `start` sends the browser to Google, `callback` receives it back.
 */
export const AUTH_ROUTES = {
  googleStart: `${API_PREFIX}/auth/google/start`,
  googleCallback: `${API_PREFIX}/auth/google/callback`,
} as const;

/**
 * Fragment parameters of the page the callback redirects to: a single-use handoff code to
 * redeem for the session token, or the error code of a failed sign-in.
 */
export const AUTH_FRAGMENT = { handoff: 'handoff', error: 'signin-error' } as const;

/** What a sign-in returns: the session of an account, with no character chosen yet. */
export const SignedInSchema = z.object({
  token: SessionTokenSchema,
  session: SessionSchema,
  player: PlayerSchema.nullable(),
});

/** Which ways of signing in this server offers. */
export const getAuthProviders = defineEndpoint({
  method: 'GET',
  path: `${API_PREFIX}/auth/providers`,
  access: 'none',
  response: z.object({
    /** Absolute URL that starts the Google sign-in (on the origin registered with Google). */
    google: z.object({ startUrl: z.url() }).nullable(),
    /** Local sign-in for development: seeded characters and dev identities. */
    devSignIn: z.boolean(),
  }),
});

/** Exchanges the handoff code of a completed browser sign-in for the session token. */
export const redeemSignIn = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/auth/handoff`,
  access: 'none',
  body: z.object({ code: z.string().min(16).max(128) }),
  response: SignedInSchema,
});

/** Signs in as a named development identity, through the same path as a real provider. */
export const devSignIn = defineEndpoint({
  method: 'POST',
  path: `${DEV_PREFIX}/auth/sign-in`,
  access: 'dev',
  successStatus: 201,
  body: z.object({ subject: z.string().trim().min(1).max(64) }),
  response: SignedInSchema,
});
